import React from 'react';
import Avatar from './Avatar';
import Tag from './Tag';
import Typography from './Typography';
import type { FontSize, FontWeight } from './Typography/Text';
import { getFontSize, getFontWeight } from './Typography/textStyles';

export interface AppLogoProps {
    /** Connector logo URL. Falls back to a coloured initial tile when missing or broken. */
    src?: string;
    /** Connector name — the accessible label, the fallback initial, and the visible text. */
    name: string;
    /** Renders the name beside the mark. Omit for a bare logo (e.g. inside a dense row). */
    showName?: boolean;
    /** Pixel size of the square mark. */
    size?: number;
    nameSize?: FontSize;
    /** Defaults to `normal`. Raise it when the logo doubles as a page title. */
    nameWeight?: FontWeight;
    /** Rendered as a tag after the name, e.g. `v1.2.0` or `Legacy`. */
    version?: string;
    /** Truncates a long name with an ellipsis and reveals the full value on hover. */
    truncate?: boolean;
    className?: string;
}

const AppLogo: React.FC<AppLogoProps> = ({
    src,
    name,
    showName = true,
    size = 24,
    nameSize = 'medium',
    nameWeight = 'normal',
    version,
    truncate = false,
    className = '',
}) => {
    const gap = size <= 18 ? 'gap-2' : 'gap-3';

    if (!showName) {
        return <Avatar src={src} name={name} size={size} shape="square" className={className} />;
    }

    return (
        // `max-w-full` is what makes `truncate` work — without it the box is floored at the name's min-content width.
        <span className={`inline-flex items-center ${gap} max-w-full ${className}`.trim()}>
            <Avatar src={src} name={name} size={size} shape="square" className="shrink-0" />
            <span className="inline-flex items-center gap-2 min-w-0">
                {truncate ? (
                    <Typography.EllipsisText
                        maxWidth="100%"
                        tooltip={name}
                        style={{
                            fontSize: getFontSize(nameSize),
                            fontWeight: getFontWeight(nameWeight),
                        }}
                    >
                        {name}
                    </Typography.EllipsisText>
                ) : (
                    <Typography.Text size={nameSize} fontWeight={nameWeight}>
                        {name}
                    </Typography.Text>
                )}
                {version ? (
                    <span className="shrink-0">
                        <Tag value={version} textTransform="none" paddingSize="compact" />
                    </span>
                ) : null}
            </span>
        </span>
    );
};

export default AppLogo;
